import { ShippingTier } from '../../types/mail';

/**
 * Base every add-on component must extend: a single `totalInCents` field so the
 * aggregate (sumNoticeAddOnPricing) can sum all components uniformly, and any new
 * add-on is automatically included without touching the sum.
 */
export type AddOnAmount = {
  totalInCents: number;
};

/**
 * Pricing structure that uses [unit price] * [qty] = [total price] pricing
 */
export type UnitAddOnPricing = AddOnAmount & {
  unitPriceInCents: number;
  qty: number;
};

/**
 * Use this function to write add-on pricing to ensure consistency for totalInCents
 */
export function makeUnitAddOnPricing(
  unitPriceInCents: number,
  qty: number
): UnitAddOnPricing {
  return { unitPriceInCents, qty, totalInCents: unitPriceInCents * qty };
}

export type NoticeAddOnPricing = {
  /**
   * Wet signature affidavit: fee is unit-priced (qty = affidavit copies) and
   * shipping is per unique address (multiple docs consolidate into one envelope per address).
   * The user selects one shipping tier for the order; it applies to wet sig shipments only.
   */
  wetSigAffidavit?: UnitAddOnPricing;

  /**
   * Shipping for wet affidavits are not done by lob so multiple copies will only
   * be charged once per address. qty = number of addresses being charged (which
   * may exclude the first address when the publisher covers the first ground
   * shipment), unitPriceInCents = the selected shipping tier's rate.
   */
  wetSigAffidavitShipping?: AddOnAmount & {
    shippingTier: ShippingTier;
    qty: number;
    unitPriceInCents: number;
  };

  /** Standard (legacy) affidavit shipping: unit-priced per mailed copy. */
  standardAffidavitShipping?: UnitAddOnPricing;

  /**
   * RON affidavit fee: flat amount per notice.
   */
  ronAffidavit?: AddOnAmount;

  /**
   * Mailed RON affidavit shipping is per document via LOB, always ground.
   * qty = number of RON affidavit copies shipped.
   */
  ronAffidavitShipping?: UnitAddOnPricing;

  /**
   * Mailed invoice shipping: per document via LOB, always ground.
   * qty = number of invoice copies shipped.
   */
  invoiceShipping?: UnitAddOnPricing;
};

/**
 * AA-program facts that affect add-on pricing but aren't part of the org's
 * flat-fee config. Derived from the publisher's affidavit reconciliation
 * settings at the pricing call site.
 */
export type NoticeAddOnPricingContext = {
  affidavitsManagedByColumn: boolean;
  /**
   * Publisher covers the first wet-signature affidavit and its ground shipment.
   * True when wet sig is required (notarizationRequired && manual vendor).
   */
  firstWetSigAffidavitFree: boolean;
};

/**
 * Org-level configuration of add-on fees (flat amounts in cents).
 */
export type NoticeAddOnPricingConfig = {
  wetSigAffidavitFeeInCents?: number;
  ronAffidavitFeeInCents?: number;
  groundShippingFeeInCents?: number;
  expressShippingFeeInCents?: number;
  rushShippingFeeInCents?: number;
};

export const DEFAULT_WET_SIG_AFFIDAVIT_FEE_IN_CENTS = 2500;
export const DEFAULT_RON_AFFIDAVIT_FEE_IN_CENTS = 1000;
export const DEFAULT_GROUND_SHIPPING_FEE_IN_CENTS = 1000;
export const DEFAULT_EXPRESS_SHIPPING_FEE_IN_CENTS = 2000;
export const DEFAULT_RUSH_SHIPPING_FEE_IN_CENTS = 4500;

/**
 * Every add-on fee resolved to a number, so a stored copy stays worth what it was
 * worth: an unset fee otherwise means "whatever Column's default is today", and a
 * later change to these constants would reprice notices placed before it.
 */
export const resolveNoticeAddOnPricingConfig = (
  config: NoticeAddOnPricingConfig | null | undefined
): Required<NoticeAddOnPricingConfig> => ({
  wetSigAffidavitFeeInCents:
    config?.wetSigAffidavitFeeInCents ?? DEFAULT_WET_SIG_AFFIDAVIT_FEE_IN_CENTS,
  ronAffidavitFeeInCents:
    config?.ronAffidavitFeeInCents ?? DEFAULT_RON_AFFIDAVIT_FEE_IN_CENTS,
  groundShippingFeeInCents:
    config?.groundShippingFeeInCents ?? DEFAULT_GROUND_SHIPPING_FEE_IN_CENTS,
  expressShippingFeeInCents:
    config?.expressShippingFeeInCents ?? DEFAULT_EXPRESS_SHIPPING_FEE_IN_CENTS,
  rushShippingFeeInCents:
    config?.rushShippingFeeInCents ?? DEFAULT_RUSH_SHIPPING_FEE_IN_CENTS
});
